import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const cmc = axios.create({
  baseURL: `${API}/api/cmc`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

cmc.interceptors.request.use((config) => {
  const orgId = localStorage.getItem('crm_org_id');
  if (orgId) config.headers['X-Org-Id'] = orgId;
  return config;
});

cmc.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) window.location.href = '/login';
    return Promise.reject(error);
  }
);

// Health
export const getCmcHealth = () => cmc.get('/health');

// Cooperators
export const listCooperators = (params = {}) => cmc.get('/cooperators', { params });
export const getCooperator = (id) => cmc.get(`/cooperators/${id}`);
export const createCooperator = (data) => cmc.post('/cooperators', data);
export const updateCooperator = (id, data) => cmc.patch(`/cooperators/${id}`, data);
export const archiveCooperator = (id) => cmc.delete(`/cooperators/${id}`);
export const linkCooperatorPortalUser = (id, portal_user_id) => cmc.post(`/cooperators/${id}/link-portal-user`, { portal_user_id });
export const listAvailablePortalUsers = () => cmc.get('/portal-users-available');
export const syncFromCrm = (data) => cmc.post('/sync-from-crm', data);

// UCC
export const listUcc = (params = {}) => cmc.get('/ucc', { params });
export const getUcc = (id) => cmc.get(`/ucc/${id}`);
export const createUcc = (data) => cmc.post('/ucc', data);
export const updateUcc = (id, data) => cmc.patch(`/ucc/${id}`, data);

// Rules
export const listRules = (params = {}) => cmc.get('/rules', { params });
export const createRule = (data) => cmc.post('/rules', data);
export const updateRule = (id, data) => cmc.patch(`/rules/${id}`, data);

// Contracts CIPAESSE / CIPASCOP / APPORT_INDUSTRIE
export const listContracts = (params = {}) => cmc.get('/contracts', { params });
export const getContract = (id) => cmc.get(`/contracts/${id}`);
export const createContract = (data) => cmc.post('/contracts', data);
export const updateContract = (id, data) => cmc.patch(`/contracts/${id}`, data);
export const validateContract = (id) => cmc.post(`/contracts/${id}/validate`);
export const signContract = (id, data) => cmc.post(`/contracts/${id}/sign`, data);
export const archiveContract = (id) => cmc.post(`/contracts/${id}/archive`);
export const unarchiveContract = (id) => cmc.post(`/contracts/${id}/unarchive`);
export const previewContractPdf = (id) => cmc.get(`/contracts/${id}/preview`, { responseType: 'blob' });
export const downloadContract = (id) => cmc.get(`/contracts/${id}/download`, { responseType: 'blob' });

// Payments
export const listCmcPayments = (params = {}) => cmc.get('/payments', { params });
export const getCmcPayment = (id) => cmc.get(`/payments/${id}`);
export const createCmcPayment = (data) => cmc.post('/payments', data);
export const updateCmcPayment = (id, data) => cmc.patch(`/payments/${id}`, data);
export const submitPayment = (id) => cmc.post(`/payments/${id}/submit`);
export const approvePayment = (id, data = {}) => cmc.post(`/payments/${id}/approve`, data);
export const rejectPayment = (id, data) => cmc.post(`/payments/${id}/reject`, data);
export const markPaymentPaid = (id, data) => cmc.post(`/payments/${id}/mark-paid`, data);

// Validations
export const listValidations = (params = {}) => cmc.get('/validations', { params });
export const createValidation = (data) => cmc.post('/validations', data);

// Attestations
export const listAttestations = (params = {}) => cmc.get('/attestations', { params });
export const getAttestation = (id) => cmc.get(`/attestations/${id}`);
export const createAttestation = (data) => cmc.post('/attestations', data);
export const updateAttestation = (id, data) => cmc.patch(`/attestations/${id}`, data);
export const issueAttestation = (id) => cmc.post(`/attestations/${id}/issue`);
export const revokeAttestation = (id, data) => cmc.post(`/attestations/${id}/revoke`, data);
export const getAttestationHtmlUrl = (id) => `${API}/api/cmc/attestations/${id}/html`;
export const getAttestationPdfUrl = (id) => `${API}/api/cmc/attestations/${id}/pdf`;
export const getAttestationQrUrl = (id) => `${API}/api/cmc/attestations/${id}/qr`;

// Verify (public)
export const verifyAttestation = (token) =>
  axios.get(`${API}/api/cmc/verify/${token}`, { withCredentials: false });

// Audit
export const listCmcAudit = (params = {}) => cmc.get('/audit', { params });

// Indicators
export const getSocialUtilityIndicators = (params = {}) =>
  cmc.get('/indicators/social-utility', { params });
export const createSocialUtilityIndicator = (data) =>
  cmc.post('/indicators/social-utility', data);

// Governance
export const getGovernanceSummary = () => cmc.get('/governance/summary');

// OpenAPI YAML
export const getOpenApiYamlUrl = () => `${API}/api/cmc/openapi.yaml`;

// ---------- Reference enums (match backend Literal definitions) ----------
export const COLLEGES = [
  { value: 'A_PORTEURS', label: 'A - Porteurs' },
  { value: 'B_SALARIES', label: 'B - Salariés' },
  { value: 'C_PARTENAIRES', label: 'C - Partenaires' },
  { value: 'D_FEDERATIONS_SYNDICATS_GE', label: 'D - Fédérations / Syndicats / GE' },
  { value: 'E_DEVELOPPEURS_CONTRIBUTEURS', label: 'E - Développeurs / Contributeurs' },
  { value: 'F_UTILISATEURS_BENEFICIAIRES', label: 'F - Utilisateurs / Bénéficiaires' },
  { value: 'G_BRANCHES_ETABLISSEMENTS', label: 'G - Branches / Établissements' },
];

export const PAYMENT_QUALIFICATIONS = [
  { value: 'PARTICIPATION_EXCEDENTS', label: 'Participation aux excédents' },
  { value: 'AVANCE_PROVISIONNELLE_EXCEDENTS', label: 'Avance provisionnelle' },
  { value: 'REMBOURSEMENT_FRAIS', label: 'Remboursement de frais' },
  { value: 'AIDE_INDIVIDUALISEE_CMC', label: 'Aide individualisée CMC' },
  { value: 'SOUTIEN_PREVENTION_SANTE_NON_ASSURANTIEL', label: 'Soutien prévention santé' },
  { value: 'SOUTIEN_SOCIAL', label: 'Soutien social' },
  { value: 'SOUTIEN_FAMILIAL', label: 'Soutien familial' },
  { value: 'SOUTIEN_EXCEPTIONNEL', label: 'Soutien exceptionnel' },
  { value: 'FRAIS_CONTRIBUTEUR', label: 'Frais contributeur' },
  { value: 'FORMATION_PARCOURS_COOPERATIF', label: 'Formation / parcours coopératif' },
  { value: 'JETON_GOUVERNANCE', label: 'Jeton de gouvernance' },
  { value: 'AUTRE_PAIEMENT_COOPERATIF', label: 'Autre paiement coopératif' },
];

export const COOPERATOR_TYPES = [
  { value: 'personne_physique', label: 'Personne physique' },
  { value: 'personne_morale', label: 'Personne morale' },
];

export const COOPERATOR_STATUSES = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'active', label: 'Actif' },
  { value: 'suspended', label: 'Suspendu' },
  { value: 'removed', label: 'Archivé' },
];

export const PAYMENT_STATUSES = [
  { value: 'draft', label: 'Brouillon', color: 'gray' },
  { value: 'submitted', label: 'Soumis', color: 'blue' },
  { value: 'under_review', label: 'En revue', color: 'amber' },
  { value: 'approved', label: 'Approuvé', color: 'emerald' },
  { value: 'rejected', label: 'Rejeté', color: 'red' },
  { value: 'paid', label: 'Payé', color: 'green' },
];

export const CONTRACT_STATUSES = [
  { value: 'draft', label: 'Brouillon', color: 'gray' },
  { value: 'validated', label: 'Validé', color: 'blue' },
  { value: 'signed', label: 'Signé', color: 'emerald' },
  { value: 'terminated', label: 'Résilié', color: 'red' },
];

export const ATTESTATION_STATUSES = [
  { value: 'draft', label: 'Brouillon', color: 'gray' },
  { value: 'issued', label: 'Émise', color: 'emerald' },
  { value: 'revoked', label: 'Révoquée', color: 'red' },
];
